import React from 'react';
import { FormattedMessage } from 'react-intl';
import messages from '../bento-box/messages';
import { STATUS_LEVELS } from './constants';

/**
 * Helper function to create star earning headings with consistent structure
 * @param {Object} message - The message object to use
 * @param {Object} values - Additional values for the message
 * @returns {JSX.Element} Formatted heading
 */
const createStarHeading = (message, values = {}) => (
  <React.Fragment>
    <FormattedMessage
      {...message}
      values={{
        starText: <span className="hiddenVisually"> star</span>,
        br: <br />,
        ...values,
      }}
    />
  </React.Fragment>
);

/**
 * Compartments configuration by status level
 * @param {string} imageBaseUrl - Base URL for images
 * @returns {Object} Object containing compartments for each status level
 */
const getCompartments = (imageBaseUrl) => ({
  [STATUS_LEVELS.GREEN]: [
    {
      image: `${imageBaseUrl}/bento-box/stars-green.svg`,
      heading: createStarHeading(messages.earnStarsHeading, {
        multiplier: '1',
      }),
    },
    {
      image: `${imageBaseUrl}/bento-box/cup-muffin-green.svg`,
      heading: (
        <FormattedMessage
          {...messages.birthdayTreatHeading}
          values={{ br: <br /> }}
        />
      ),
      body: <FormattedMessage {...messages.birthdayTreatBody} />,
      cta: {
        label: <FormattedMessage {...messages.learnMoreCta} />,
        visualStyle: 'textLink',
        href: '/rewards/benefits',
      },
    },
    {
      image: `${imageBaseUrl}/bento-box/stars-green.svg`,
      heading: (
        <FormattedMessage
          {...messages.freeMondayHeading}
          values={{ br: <br /> }}
        />
      ),
      body: <FormattedMessage {...messages.freeMondayBody} />,
    },
  ],
  [STATUS_LEVELS.GOLD]: [
    {
      image: `${imageBaseUrl}/bento-box/stars-gold.svg`,
      heading: createStarHeading(messages.goldEarnStarsHeading),
    },
    {
      image: `${imageBaseUrl}/bento-box/heart-hand-gold.svg`,
      heading: (
        <FormattedMessage
          {...messages.starsNeverExpireHeading}
          values={{ br: <br /> }}
        />
      ),
    },
    {
      image: `${imageBaseUrl}/bento-box/cup-muffin-gold.svg`,
      heading: (
        <FormattedMessage
          {...messages.goldBirthdayTreatHeading}
          values={{ br: <br /> }}
        />
      ),
      body: <FormattedMessage {...messages.birthdayTreatBody} />,
      cta: {
        label: <FormattedMessage {...messages.learnMoreCta} />,
        visualStyle: 'textLink',
        href: '/rewards/benefits',
      },
    },
    {
      image: `${imageBaseUrl}/bento-box/card-gold.svg`,
      heading: (
        <FormattedMessage
          {...messages.goldCardHeading}
          values={{ br: <br /> }}
        />
      ),
      body: <FormattedMessage {...messages.goldCardBody} />,
    },
  ],
  [STATUS_LEVELS.RESERVE]: [
    {
      image: `${imageBaseUrl}/bento-box/stars-gold.svg`,
      heading: createStarHeading(messages.reserveEarnStarsHeading),
    },
    {
      image: `${imageBaseUrl}/bento-box/heart-hand-gold.svg`,
      heading: (
        <FormattedMessage
          {...messages.starsNeverExpireHeading}
          values={{ br: <br /> }}
        />
      ),
    },
    {
      image: `${imageBaseUrl}/bento-box/cup-muffin-gold.svg`,
      heading: (
        <FormattedMessage
          {...messages.reserveBirthdayTreatHeading}
          values={{ br: <br /> }}
        />
      ),
      body: <FormattedMessage {...messages.birthdayTreatBody} />,
      cta: {
        label: <FormattedMessage {...messages.learnMoreCta} />,
        visualStyle: 'textLink',
        href: '/rewards/benefits',
      },
    },
    {
      image: `${imageBaseUrl}/bento-box/card-gold.svg`,
      heading: (
        <FormattedMessage
          {...messages.reserveCardHeading}
          values={{ br: <br /> }}
        />
      ),
      body: <FormattedMessage {...messages.reserveCardBody} />,
    },
    {
      image: `${imageBaseUrl}/bento-box/cup-muffin-gold.svg`,
      heading: (
        <FormattedMessage
          {...messages.originCollectionHeading}
          values={{ br: <br /> }}
        />
      ),
      body: <FormattedMessage {...messages.originCollectionBody} />,
    },
  ],
});

/**
 * Get compartments based on rewards level
 * @param {string} rewardsLevel - The rewards level (STATUS_LEVELS.GREEN, STATUS_LEVELS.GOLD, STATUS_LEVELS.RESERVE)
 * @param {string} imageBaseUrl - Base URL for images
 * @returns {Array} Array of compartment objects
 */
export const getCompartmentsByLevel = (rewardsLevel, imageBaseUrl) => {
  const allCompartments = getCompartments(imageBaseUrl);
  const levelCompartments = allCompartments[rewardsLevel];

  // Fallback to GREEN level if the requested level doesn't exist
  return levelCompartments || allCompartments[STATUS_LEVELS.GREEN];
};
